import TableGripsUI from 'src/views/table-grips';

import { getDimensionsFromElement } from '../utils/dimensions';

import TableSelection from './table-selection';

export default class TableGrips {
  app = null;
  canvas = null;
  tableElement = null;
  tableSelectionInstance = null;
  gripSize = 10;
  gutterSize = 40;
  maxGripSizePercentage = 2.5;
  removeCellClassName = 'table-cell-remove-selected';

  constructor () {
    this.canvas = INKIVE.Tool.getInstance().editor.canvas;
  }

  getGripSize () {
    return this.gripSize / this.canvas.getZoom();
  }

  getGutterSize () {
    return this.gutterSize / this.canvas.getZoom();
  }

  createGripsWrapper (dimensions, gripSize, gutterSize) {
    const size = {
      x: dimensions.x + gripSize - gutterSize,
      y: dimensions.y + gripSize - gutterSize,
      width: dimensions.width - gripSize + gutterSize,
      height: dimensions.height - gripSize + gutterSize
    };

    return this.canvas.addSvgElementFromJson({
      element: 'foreignObject',
      attr: {
        id: 'table-grip',
        'data-sanitize': 'ignore-save',
        overflow: 'visible',
        ...size
      }
    });
  }

  initEditMode = () => {
    if (!INKIVE.Table.tableSelectionInstance) {
      INKIVE.Table.beginTableEditingMode(this.tableElement);
    }
  };

  updateGrips = () => {
    this.app.closeApp();
    this.buildGrips();
  };

  buildGrips (tableElement) {
    this.tableElement = tableElement ?? this.tableElement;
    this.tableSelectionInstance = new TableSelection(this.tableElement);

    this.removeGrips({ removeTableSelection: false });

    this.app = this.buildReactiveGrips(this.tableElement);
    this.app.mountApp();

    this.initListeners();
  }

  buildReactiveGrips (tableElement) {
    const gripSize = this.getGripSize();
    const gutterSize = this.getGutterSize();
    const dimensions = getDimensionsFromElement(tableElement.querySelector('rect'));
    const strokeWidth = Number(tableElement.querySelector('.table-borders .table-border.index-c-0').getAttribute('stroke-width'));
    const halfStrokeWidth = strokeWidth / 2;

    dimensions.x = dimensions.x - halfStrokeWidth - gripSize;
    dimensions.y = dimensions.y - halfStrokeWidth - gripSize;
    dimensions.width = dimensions.width + strokeWidth + gripSize;
    dimensions.height = dimensions.height + strokeWidth + gripSize;

    const container = this.createGripsWrapper(dimensions, gripSize, gutterSize);
    tableElement.querySelector('rect')
      .insertAdjacentElement('afterend', container);

    return new TableGripsUI({
      appId: 'table-grip-ui',
      element: tableElement,
      targetNodeSelector: `#${container.id}`,
      gripsSize: gripSize,
      gutterSize,
      dimensions,
      initEditMode: this.initEditMode,
      onRemoveRow: (event) => {
        event.stopPropagation();

        this.initEditMode();
        INKIVE.Table.deleteRow();
      },
      onRemoveColumn: (event) => {
        event.stopPropagation();

        this.initEditMode();
        INKIVE.Table.deleteColumn();
      },
      onRemoveMouseEnter: (event, type, index) => {
        const selector = type === 'row'
          ? `.table-cell.index-r-${index} .textarea > rect`
          : `.table-cell.index-c-${index} .textarea > rect`;
        const cells = tableElement.querySelectorAll(selector);

        cells.forEach(cell => cell.classList.add(this.removeCellClassName));
      },
      onRemoveMouseLeave: () => {
        tableElement.querySelectorAll(`.${this.removeCellClassName}`)
          .forEach(cell => cell.classList.remove(this.removeCellClassName));
      },
      onInsertRow: (event, insertBefore, position) => {
        event.stopPropagation();

        this.initEditMode();
        INKIVE.Table.insertRow(insertBefore, position);
      },
      onInsertColumn: (event, insertBefore, position) => {
        event.stopPropagation();

        this.initEditMode();
        INKIVE.Table.insertColumn(insertBefore, position);
      },
      onGripClick: (event, type, data) => {
        event.stopPropagation();

        this.initEditMode();

        switch (type) {
          case 'corner':
            this.tableClickHandler(event, data);
            break;
          case 'row':
            this.rowClickHandler(event, data);
            break;
          case 'column':
            this.columnClickHandler(event, data);
            break;
        }
      }
    });
  }

  removeGrips ({
    removeTableSelection = true
  } = {}) {
    if (removeTableSelection) {
      this.tableSelectionInstance.clear();
    }

    this.app?.closeApp();
    this.app = null;

    this.tableElement.querySelector('#table-grip')?.remove();

    INKIVE.DesignFacade.off('table.update', this.updateGrips);
  }

  /**
   * Event Listeners
   */

  initListeners () {
    INKIVE.DesignFacade.on('table.update', this.updateGrips);
  }

  tableClickHandler (event, data) {
    const target = this.tableElement.querySelectorAll('.table-cell');
    const areAllSelected = [...target].every(cell => cell.querySelector('rect').classList.contains('table-cell-selected'));

    this.tableSelectionInstance.clear();

    if (event.shiftKey && areAllSelected) {
      this.tableSelectionInstance.unselect(target);
    } else {
      this.tableSelectionInstance.select(target);
      INKIVE.Table.tableSelectionInstance = INKIVE.Table.tableSelectionInstance ?? this.tableSelectionInstance;
      INKIVE.Table.tableSelectionInstance.numberOfSelectedCells = this.tableSelectionInstance.numberOfSelectedCells;
    }
  }

  rowClickHandler (event, position) {
    const target = this.tableElement.querySelectorAll(`.table-row:nth-of-type(${position}) .table-cell`);
    const areAllSelected = [...target].every(cell => cell.querySelector('rect').classList.contains('table-cell-selected'));

    if (!event.shiftKey) {
      this.tableSelectionInstance.clear();
    }

    if (event.shiftKey && areAllSelected) {
      this.tableSelectionInstance.unselect(target);
    } else {
      this.tableSelectionInstance.select(target);
    }
  }

  columnClickHandler (event, position) {
    const target = this.tableElement.querySelectorAll(`.table-row .table-cell:nth-of-type(${position})`);
    const areAllSelected = [...target].every(cell => cell.querySelector('rect').classList.contains('table-cell-selected'));

    if (!event.shiftKey) {
      this.tableSelectionInstance.clear();
    }

    if (event.shiftKey && areAllSelected) {
      this.tableSelectionInstance.unselect(target);
    } else {
      this.tableSelectionInstance.select(target);
    }
  }
}
